import React, { useEffect, useState } from "react";
import { Flame, Trophy, Target } from "lucide-react";
import { getQuizState, getTodayResult, recordQuizResult, currentStreak } from "@/lib/quizStorage";
import { StreakShare } from "@/components/StreakShare";
import { FaceOffLeaderboard } from "@/components/FaceOffLeaderboard";
import { getPlayer } from "@/lib/player";
import api from "@/lib/api";

const KEY = "ot_headline_guess_v1";
const MIN = 1850;
const MAX = new Date().getFullYear();
export const scoreFor = (guess, actual) => { const d = Math.abs(guess - actual); return d === 0 ? 100 : d === 1 ? 80 : d <= 3 ? 60 : d <= 5 ? 40 : d <= 10 ? 20 : 0; };
const verdict = (s) => (s === 100 ? "Spot on!" : s >= 80 ? "One year out — sharp." : s >= 60 ? "Close — within three years." : s >= 40 ? "Not bad, within five." : s >= 20 ? "Within a decade." : "Way off — history is sneaky.");

/** "Did you guess the year?" — one headline a day (Hall of Fame when it has a year, else the day's top weird-history pick). Streak like the quiz. */
export const HeadlineGuess = ({ target, dateKey, onAnswered, revealRequested = false }) => {
  const [year, setYear] = useState(1950);
  const [result, setResult] = useState(() => getTodayResult(dateKey, KEY));
  const [state, setState] = useState(() => getQuizState(KEY));
  const [board, setBoard] = useState(null);
  const postScore = (r) => {
    const player = getPlayer();
    api.post("/headline-guess/score", { date: dateKey, score: r.score, streak: currentStreak(dateKey, KEY), revealed: !!r.revealed, player_id: player.id, player_name: player.name || undefined })
      .then((res) => setBoard(res.data.leaderboard)).catch(() => {});
  };
  useEffect(() => { const r = getTodayResult(dateKey, KEY); setResult(r); setState(getQuizState(KEY)); if (r && onAnswered) onAnswered(r); }, [dateKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (result) api.get("/headline-guess/leaderboard", { params: { date: dateKey, player_id: getPlayer().id } }).then((r) => setBoard(r.data)).catch(() => {});
  }, [dateKey, !!result]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!revealRequested || result || !target?.year) return;
    const r = { guess: null, year: target.year, score: 0, total: 0, text: target.text, revealed: true };
    setState(recordQuizResult(dateKey, r, KEY));
    setResult(r);
    onAnswered?.(r);
    postScore(r);
  }, [revealRequested]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!target?.year) return null;
  const submit = () => {
    const score = scoreFor(year, target.year);
    const r = { guess: year, year: target.year, score, total: score, text: target.text };
    setState(recordQuizResult(dateKey, r, KEY));
    setResult(r);
    onAnswered?.(r);
    postScore(r);
  };
  const streak = currentStreak(dateKey, KEY);
  const dateLabel = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long" });
  return (
    <div data-testid="headline-guess" className="mt-4 border-2 border-ink bg-[#fbf8f1] p-4 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold flex items-center gap-1.5"><Target className="w-3.5 h-3.5" /> Did you guess the year?</p>
        <div className="flex items-center gap-2 text-[11px] font-semibold text-ink-muted">
          <span data-testid="headline-guess-streak" className="inline-flex items-center gap-1 px-2 py-0.5 border border-ink/20 bg-white"><Flame className="w-3 h-3 text-gold" /> Streak {streak} day{streak === 1 ? "" : "s"}</span>
          <span data-testid="headline-guess-best" className="inline-flex items-center gap-1 px-2 py-0.5 border border-ink/20 bg-white"><Trophy className="w-3 h-3 text-gold" /> Best {state.best || 0}</span>
        </div>
      </div>
      {!result ? (
        <>
          <p className="mt-2 text-[14px] text-ink">{target.kind === "clipping" ? <>The newspaper page above — <span className="font-semibold">{target.text}</span> — was printed in which year? Slide to your guess.</> : <>The headline above — <span className="font-semibold">“{target.text}”</span> — was printed in which year? Slide to your guess.</>}</p>
          <div className="mt-3 flex items-center gap-4">
            <input type="range" min={MIN} max={MAX} value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="headline-guess-slider" className="flex-1 accent-ink" aria-label="Guess the year" />
            <span data-testid="headline-guess-year" className="w-16 text-right text-2xl font-bold text-ink tabular-nums">{year}</span>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input type="number" min={MIN} max={MAX} value={year} onChange={(e) => setYear(Math.max(MIN, Math.min(MAX, Number(e.target.value) || MIN)))} data-testid="headline-guess-input" className="w-24 h-10 border border-ink/30 bg-white px-3 text-sm text-ink" />
            <button type="button" onClick={submit} data-testid="headline-guess-submit" className="inline-flex items-center px-5 h-10 bg-ink text-white text-sm font-semibold hover:bg-gold hover:text-ink transition-colors">Lock in {year}</button>
          </div>
        </>
      ) : (
        <div data-testid="headline-guess-result" className="mt-2">
          <div className="flex flex-wrap items-center gap-4">
            <span className={`px-3 py-1.5 text-[18px] font-bold ${result.score >= 80 ? "bg-gold text-ink" : result.score >= 40 ? "bg-white border border-ink text-ink" : "bg-ink/5 text-ink-muted"}`}>+{result.score}</span>
            <p className="text-[14px] text-ink">{result.revealed ? <>You peeked — it was <span className="font-bold text-gold">{result.year}</span>. No points today, but the streak lives on tomorrow.</> : <>{verdict(result.score)} It was <span className="font-bold text-gold">{result.year}</span>; you said {result.guess}{result.guess !== result.year ? ` (${Math.abs(result.guess - result.year)} yr${Math.abs(result.guess - result.year) === 1 ? "" : "s"} out)` : ""}. Come back tomorrow for a new {target.kind === "clipping" ? "page" : "headline"}.</>}</p>
          </div>
          <StreakShare result={result} streak={streak} kind={target.kind} dateLabel={dateLabel} />
          <FaceOffLeaderboard board={board} onBoard={setBoard} dateKey={dateKey} kind="headline" />
        </div>
      )}
    </div>
  );
};
